import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { documentsApi, studentsApi } from '../api';
import {
  Badge,
  DataTable,
  EmptyState,
  Field,
  HeroPanel,
  Panel,
  PortalShell,
  StatCard,
  TextInput,
  getSessionUser
} from '../components/PortalUI';
import config from '../config';
import { dateOnly, downloadCsv, grade, percent, qrImage } from '../utils';

const navItems = ['Overview', 'Profile', 'Exam Records', 'Documents', 'Verification'];
const blankExam = { subject: '', score: '', totalMarks: '', examDate: '' };

function StudentDashboard() {
  const [active, setActive] = useState('Overview');
  const [profile, setProfile] = useState(null);
  const [exams, setExams] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [newExam, setNewExam] = useState(blankExam);
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);
  const userId = getSessionUser();
  const verifyUrl = `${config.APP_URL}/verify/student/${userId}`;

  const load = useCallback(async () => {
    setLoading(true);
    const [profileData, examData, docData] = await Promise.all([
      studentsApi.get(userId).catch(() => null),
      studentsApi.exams(userId).catch(() => []),
      documentsApi.list(userId).catch(() => [])
    ]);
    setProfile(profileData);
    setExams(Array.isArray(examData) ? examData : []);
    setDocuments(Array.isArray(docData) ? docData : []);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(() => {
    const scores = exams.map((exam) => percent(exam.score, exam.totalMarks));
    const average = scores.length ? Number((scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1)) : 0;
    const best = exams.reduce((top, exam) => percent(exam.score, exam.totalMarks) > percent(top.score || 0, top.totalMarks || 1) ? exam : top, {});
    return { average, best, grade: grade(average) };
  }, [exams]);

  const addExam = async () => {
    await studentsApi.addExam({
      studentId: userId,
      name: profile?.name,
      subject: newExam.subject,
      score: Number(newExam.score),
      totalMarks: Number(newExam.totalMarks),
      examDate: newExam.examDate
    });
    setNewExam(blankExam);
    setNotice('Exam record added');
    load();
  };

  const examColumns = [
    { key: 'subject', label: 'Subject', render: (row) => <span className="font-black text-slate-800">{row.subject}</span> },
    { key: 'score', label: 'Score' },
    { key: 'totalMarks', label: 'Total' },
    {
      key: 'percentage',
      label: 'Percentage',
      render: (row) => {
        const value = percent(row.score, row.totalMarks);
        return (
          <div className="flex items-center gap-3">
            <div className="h-2 w-28 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-blue-600" style={{ width: `${value}%` }} />
            </div>
            <span className="font-bold">{value}%</span>
          </div>
        );
      }
    },
    { key: 'grade', label: 'Grade', render: (row) => <Badge tone={percent(row.score, row.totalMarks) >= 60 ? 'emerald' : 'rose'}>{grade(percent(row.score, row.totalMarks)).label}</Badge> },
    { key: 'date', label: 'Date', render: (row) => row.recordType?.replace('EXAM#', '') || '-' }
  ];

  return (
    <PortalShell
      role="student"
      title={active}
      subtitle="Your academic profile, exam performance, documents and verification tools in one secure workspace."
      navItems={navItems}
      active={active}
      onNavChange={setActive}
      actions={<button onClick={load} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50">Refresh</button>}
    >
      <div className="space-y-6">
        <HeroPanel
          role="student"
          eyebrow="Academic record"
          title={`Welcome back, ${profile?.name || userId}`}
          right={<img src={qrImage(verifyUrl)} alt="Student verification QR" className="h-28 w-28 rounded-lg bg-white p-2 shadow-lg" />}
        >
          {profile?.department || 'Department'} department, Year {profile?.year || '-'} student profile connected to CloudRecords verification.
        </HeroPanel>

        {notice && <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700">{notice}</div>}

        {active === 'Overview' && (
          <>
            <div className="grid gap-4 md:grid-cols-4">
              <StatCard label="Exams Taken" value={exams.length} tone="text-blue-600" />
              <StatCard label="Average Score" value={summary.average ? `${summary.average}%` : 'N/A'} tone="text-emerald-600" />
              <StatCard label="Predicted Grade" value={summary.grade.label} tone={summary.grade.color} />
              <StatCard label="Documents" value={documents.length} tone="text-violet-600" />
            </div>
            <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
              <Panel title="Performance Trend" subtitle="Subject-wise percentage overview.">
                {exams.length ? (
                  <div className="space-y-4">
                    {exams.map((exam) => {
                      const value = percent(exam.score, exam.totalMarks);
                      return (
                        <div key={`${exam.subject}-${exam.recordType}`} className="grid grid-cols-[120px_1fr_56px] items-center gap-3 text-sm">
                          <span className="truncate font-black text-slate-700">{exam.subject}</span>
                          <div className="h-3 overflow-hidden rounded-full bg-slate-100">
                            <div className="h-full rounded-full bg-blue-600" style={{ width: `${value}%` }} />
                          </div>
                          <span className="text-right font-black text-slate-700">{value}%</span>
                        </div>
                      );
                    })}
                  </div>
                ) : <EmptyState title="No exam records" text="Marks added by you or teachers will appear here." />}
              </Panel>
              <Panel title="Student Identity" subtitle="Verified profile summary.">
                <div className="space-y-3 text-sm">
                  {[
                    ['Student ID', profile?.studentId || userId],
                    ['Department', profile?.department || '-'],
                    ['Year', profile?.year ? `Year ${profile.year}` : '-'],
                    ['Email', profile?.email || '-'],
                    ['Enrolled', dateOnly(profile?.createdAt)]
                  ].map(([label, value]) => (
                    <div key={label} className="flex justify-between gap-4 border-b border-slate-100 pb-2">
                      <span className="text-slate-400">{label}</span>
                      <span className="text-right font-black text-slate-800">{value}</span>
                    </div>
                  ))}
                </div>
              </Panel>
            </div>
          </>
        )}

        {active === 'Profile' && (
          <Panel title="My Profile" subtitle="Your institutional identity and contact information.">
            {profile ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {Object.entries({
                  'Full Name': profile.name,
                  'Student ID': profile.studentId,
                  Department: profile.department,
                  Year: profile.year ? `Year ${profile.year}` : '-',
                  Email: profile.email,
                  'Enrolled On': dateOnly(profile.createdAt)
                }).map(([label, value]) => (
                  <div key={label} className="rounded-lg bg-slate-50 p-4">
                    <p className="text-xs font-black uppercase text-slate-400">{label}</p>
                    <p className="mt-1 break-words font-bold text-slate-800">{value || '-'}</p>
                  </div>
                ))}
              </div>
            ) : <EmptyState title="Profile not found" text="Please contact your administrator." />}
          </Panel>
        )}

        {active === 'Exam Records' && (
          <Panel
            title="Exam Records"
            subtitle="Track scores, grade bands and exam dates."
            actions={<button onClick={() => downloadCsv('student-exams.csv', exams)} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-black text-white hover:bg-blue-700">Export CSV</button>}
          >
            <div className="mb-5 grid gap-3 lg:grid-cols-[1fr_1fr_1fr_1fr_auto]">
              {Object.keys(blankExam).map((key) => (
                <Field key={key} label={key}>
                  <TextInput value={newExam[key]} onChange={(e) => setNewExam({ ...newExam, [key]: e.target.value })} placeholder={key} className="focus:ring-blue-100 focus:border-blue-500" />
                </Field>
              ))}
              <button onClick={addExam} className="self-end rounded-lg bg-slate-950 px-4 py-2.5 text-sm font-black text-white hover:bg-slate-800">Add Exam</button>
            </div>
            <DataTable columns={examColumns} rows={exams} loading={loading} />
          </Panel>
        )}

        {active === 'Documents' && (
          <Panel title="Documents" subtitle="Files attached to your student record.">
            {documents.length ? (
              <div className="grid gap-3 md:grid-cols-2">
                {documents.map((doc) => (
                  <a key={doc.documentId} href={doc.downloadUrl || '#'} target="_blank" rel="noreferrer" className="rounded-lg border border-slate-200 p-4 hover:bg-slate-50">
                    <p className="font-black text-slate-800">{doc.fileName}</p>
                    <p className="mt-1 text-xs font-bold text-slate-400">{doc.status} - {dateOnly(doc.createdAt)}</p>
                  </a>
                ))}
              </div>
            ) : <EmptyState title="No documents uploaded" text="Verified files will appear here when attached by admin." />}
          </Panel>
        )}

        {active === 'Verification' && (
          <Panel title="Verification QR" subtitle="Use this QR code to verify your active student record.">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
              <img src={qrImage(verifyUrl)} alt="Student verification QR" className="h-44 w-44 rounded-lg border border-slate-200 bg-white p-3 shadow-sm" />
              <div>
                <p className="text-sm text-slate-500">Public verification URL</p>
                <p className="mt-2 break-all rounded-lg bg-slate-50 p-3 text-sm font-bold text-slate-700">{verifyUrl}</p>
              </div>
            </div>
          </Panel>
        )}
      </div>
    </PortalShell>
  );
}

export default StudentDashboard;
